import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TableViewIcon from "@mui/icons-material/TableView";
import DescriptionIcon from "@mui/icons-material/Description";
import { useTranslation } from "react-i18next";
import CustomButton from "./CustomButton";
import {
  exportToCsv,
  exportToExcel,
  ExcelExportOptions,
} from "../utils/exportUtils";

interface ExportMenuProps<T> {
  data: T[];
  filename: string;
  columns: {
    id: keyof T;
    label: string;
    transform?: (value: any, row: T) => string;
  }[];
  disabled?: boolean;
  // Optional advanced export configuration
  advancedExport?: () => ExcelExportOptions;
}

export function ExportMenu<T extends Record<string, any>>({
  data,
  filename,
  columns,
  disabled = false,
  advancedExport,
}: ExportMenuProps<T>) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportCsv = () => {
    exportToCsv(data, filename, columns);
    handleClose();
  };

  const handleExportExcel = () => {
    exportToExcel(data, filename, columns);
    handleClose();
  };

  const handleExportExcelAdvanced = () => {
    if (advancedExport) {
      const { exportToExcelAdvanced } = require("../utils/exportUtils");
      const options = advancedExport();
      exportToExcelAdvanced(options);
    }
    handleClose();
  };

  return (
    <>
      <CustomButton
        variant="outline"
        icon={<FileDownloadIcon />}
        endIcon={
          <Box
            sx={{
              display: 'inline-flex',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <KeyboardArrowDownIcon />
          </Box>
        }
        onClick={handleClick}
        disabled={disabled}
      >
        {t("common.export", "Exportar")}
      </CustomButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleExportCsv}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.exportCsv', 'CSV')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <TableViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.exportExcel', 'Excel')}</ListItemText>
        </MenuItem>
        {advancedExport && (
          <MenuItem onClick={handleExportExcelAdvanced}>
            <ListItemIcon>
              <TableViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t("common.exportExcelDetailed", "Excel (Detalhado)")}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
